import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Checkbox } from 'antd';
import get from 'lodash/get';
import momentTimezone from 'moment-timezone';

import DetailRow from '../../../components/common/DetailRow';
import InlineAlert from '../../../components/common/InlineAlert';
import BookingActionBar from '../../../components/NewBooking/BookingActionBar';
import TermsAccordion, {
  TermsSection,
} from '../../../components/common/TermsAccordion';
import { Card, CardBody, SectionTitle } from '../../../components/common/Card';
import {
  LineItemDescription,
  LineItemDetail,
} from '../../../components/common/DocumentCard';
import { EstimateTag } from '../../../components/NewBooking/FeeSummary/styles';
import { bookAppointment } from '../../../API/appointment';
import { markAppointmentBooked } from '../../../slices/workflow';
import { ROUTE_PREFIX_BOOKING, ROUTES } from '../../../constants/routes';
import { ACTIVITY_TYPE, STATUS } from '../../../constants';
import {
  CONTACT_PHONE,
  SERVICE_DESCRIPTION,
} from '../../../constants/business';
import { NO_REBATE_CARD_TYPES } from '../../../constants/cardType';
import {
  BOOKING_TERMS_SECTIONS,
  CANCELLATION_POLICY_NOT_AGREED_MESSAGE,
  CANCELLATION_POLICY_SECTIONS,
  TERMS_VERSION,
} from '../../../constants/terms';
import { TIME_FORMAT } from '../../../constants/time';
import {
  transformData,
  validateBookingData,
} from '../../../utils/bookingUtils';
import {
  clearSmsTemplateId,
  getSmsTemplateId,
} from '../../../utils/smsTemplateStorage';
import {
  formatGapFeeDisplay,
  formatTotalFeeDisplay,
} from '../../../utils/feeUtils';
import { formatAppointmentMomentLabel } from '../../../utils/appointmentDisplayUtils';
import { formatMedicareStatus } from '../../../utils/medicareUtils';
import { scrollToElement } from '../../../utils/scrollUtils';
import {
  formatAddressLine,
  formatRelationshipLabel,
} from '../../../utils/bookingSummaryUtils';
import analytics from '../../../infra/analytics';
import {
  AttestationRow,
  AttestationText,
  ClinicianName,
  ClinicianSpeciality,
  ServiceLineItem,
} from './styles';

const POLICY_SECTION_KEY = CANCELLATION_POLICY_SECTIONS[0].key;
const ATTESTATION_TEXT =
  'I have reviewed the details above and confirm they are correct.';
const ATTESTATION_ERROR =
  'Please confirm you have reviewed your details before continuing.';
const BOOKING_FAILURE_MESSAGE = `Oops, something went wrong, please contact Telecare at (${CONTACT_PHONE}) or via live chat.`;

const Confirmation = () => {
  const workflow = useSelector(({ workflow }) => workflow.data);
  const bookingData = useSelector(({ booking }) => booking.data);
  const dispatch = useDispatch();
  const [bookingErrors, setBookingErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAttested, setIsAttested] = useState(false);
  const [showAttestationError, setShowAttestationError] = useState(false);
  const [isPolicyAgreed, setIsPolicyAgreed] = useState(false);
  const [showPolicyError, setShowPolicyError] = useState(false);
  const history = useHistory();
  const { workflowId } = useParams();
  const smsTemplateId = getSmsTemplateId(workflowId);

  const attestationRef = useRef(null);
  const termsRef = useRef(null);
  const bookingErrorsRef = useRef(null);

  const { specialist } = workflow;
  const { start: appointmentStart, timezone } =
    bookingData?.appointmentTime || {};
  const personalDetails = bookingData?.personalDetails || {};

  const appointmentMoment = appointmentStart
    ? momentTimezone(appointmentStart).tz(timezone)
    : null;
  const appointmentMomentLabel =
    formatAppointmentMomentLabel(appointmentMoment);
  const appointmentDateTimeLabel =
    appointmentMomentLabel && timezone
      ? `${appointmentMomentLabel} · ${timezone}`
      : appointmentMomentLabel;
  const clinicianName = specialist
    ? `Dr. ${specialist.firstName} ${specialist.lastName}`.trim()
    : '';
  const clinicianSpeciality = specialist?.speciality || '';
  const specialistFee = get(workflow, 'specialist.fee', null);
  const hasNoRebate = NO_REBATE_CARD_TYPES.includes(
    get(bookingData, 'medicare.cardType', '')
  );
  const payableLabel = hasNoRebate
    ? 'Amount payable'
    : 'Out of Pocket (Gap Fee)';
  const payableDisplay = hasNoRebate
    ? formatTotalFeeDisplay(specialistFee)
    : formatGapFeeDisplay(specialistFee);
  const payableAmountLabel = hasNoRebate
    ? payableLabel
    : `${payableLabel} \u00b7 estimate`;

  const patientFullName = `${personalDetails.firstName || ''} ${
    personalDetails.lastName || ''
  }`.trim();
  const dateOfBirthLabel = personalDetails.dateOfBirth
    ? momentTimezone(personalDetails.dateOfBirth).format(TIME_FORMAT.TABLE_TIME)
    : '';
  const medicareStatus = formatMedicareStatus(bookingData?.medicare);
  const addressLine = formatAddressLine(personalDetails);

  const nextOfKin = bookingData?.nextOfKin || {};
  const relationshipLabel = formatRelationshipLabel(nextOfKin.relationship);
  const hasNextOfKin = Boolean(
    nextOfKin.name || nextOfKin.phone || relationshipLabel
  );

  const bookingInfo = bookingData?.bookingInfo || {};
  const hasHealthInformation = Boolean(
    bookingInfo.allergies || bookingInfo.currentMedication
  );

  useEffect(() => {
    analytics.logPageViewEvent('confirm_booking_info', {
      workflow_id: workflowId,
    });
    analytics.logPageViewEvent('cancellation_policy', {
      workflow_id: workflowId,
    });
  }, [workflowId]);

  const handleAttestationChange = (event) => {
    setIsAttested(event.target.checked);
    setShowAttestationError(false);
  };

  const handlePolicyAgreementChange = (sectionKey, isAgreed) => {
    setIsPolicyAgreed(isAgreed);
    setShowPolicyError(false);
  };

  const buildConsent = () => {
    const bookingAgreements = bookingData?.bookingTerms?.agreements || {};
    const agreedBookingSectionKeys = BOOKING_TERMS_SECTIONS.filter(({ key }) =>
      Boolean(bookingAgreements[key])
    ).map(({ key }) => key);

    return {
      termsVersion: TERMS_VERSION,
      agreedSectionKeys: [...agreedBookingSectionKeys, POLICY_SECTION_KEY],
      attestation: {
        text: ATTESTATION_TEXT,
        agreed: isAttested,
      },
    };
  };

  const handleConfirm = async () => {
    setShowAttestationError(!isAttested);
    setShowPolicyError(!isPolicyAgreed);

    if (!isAttested) {
      scrollToElement(attestationRef.current);
      return false;
    }

    if (!isPolicyAgreed) {
      scrollToElement(termsRef.current);
      return false;
    }

    setIsLoading(true);
    const errors = validateBookingData(bookingData);

    if (errors.length > 0) {
      setIsLoading(false);
      setBookingErrors(errors);
      scrollToElement(bookingErrorsRef.current);
      return false;
    }

    const { status } = await bookAppointment(
      transformData(workflowId, bookingData, smsTemplateId, buildConsent())
    );

    if (status === STATUS.SUCCESS) {
      // Back or a /new-booking link now shows the booked appointment
      // instead of offering this booking a second time.
      dispatch(
        markAppointmentBooked({
          workflowId,
          startDateTime: bookingData.appointmentTime.start,
          endDateTime: bookingData.appointmentTime.end,
        })
      );
      clearSmsTemplateId(workflowId);
      analytics.logPageViewEvent('booking_result', {
        booking_result: 'success',
        workflow_id: workflowId,
      });
      history.push(
        `${ROUTE_PREFIX_BOOKING}/${workflowId}${ROUTES.NOTIFICATION}?type=${ACTIVITY_TYPE.CONFIRM}`
      );
      return true;
    }

    analytics.logPageViewEvent('booking_result', {
      booking_result: 'failure',
      workflow_id: workflowId,
    });
    setBookingErrors([BOOKING_FAILURE_MESSAGE]);
    setIsLoading(false);
    return false;
  };

  return (
    <>
      <Card>
        <CardBody>
          <ServiceLineItem>
            <div>
              <LineItemDescription>{SERVICE_DESCRIPTION}</LineItemDescription>
              {clinicianName && <ClinicianName>{clinicianName}</ClinicianName>}
              {clinicianSpeciality && (
                <ClinicianSpeciality>{clinicianSpeciality}</ClinicianSpeciality>
              )}
              {appointmentDateTimeLabel && (
                <LineItemDetail>{appointmentDateTimeLabel}</LineItemDetail>
              )}
            </div>
          </ServiceLineItem>
          {payableDisplay && (
            <DetailRow
              label={payableLabel}
              badge={hasNoRebate ? null : <EstimateTag>Estimate</EstimateTag>}
              value={payableDisplay}
              currency="AUD"
              emphasised
            />
          )}
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <SectionTitle>Your details</SectionTitle>

          {patientFullName && (
            <DetailRow
              label="PATIENT First Name / Surname"
              value={patientFullName}
            />
          )}
          {dateOfBirthLabel && (
            <DetailRow label="Date Of Birth" value={dateOfBirthLabel} />
          )}
          {personalDetails.mobile && (
            <DetailRow label="Mobile" value={personalDetails.mobile} />
          )}
          {personalDetails.email && (
            <DetailRow label="Email" value={personalDetails.email} />
          )}
          {addressLine && <DetailRow label="Address" value={addressLine} />}
          {medicareStatus && (
            <DetailRow
              label={medicareStatus.label}
              value={medicareStatus.value}
            />
          )}
        </CardBody>
      </Card>

      {hasNextOfKin && (
        <Card>
          <CardBody>
            <SectionTitle>Next of kin</SectionTitle>

            {nextOfKin.name && (
              <DetailRow label="Name" value={nextOfKin.name} />
            )}
            {nextOfKin.phone && (
              <DetailRow label="Mobile Number" value={nextOfKin.phone} />
            )}
            {relationshipLabel && (
              <DetailRow
                label="Relationship to You"
                value={relationshipLabel}
              />
            )}
          </CardBody>
        </Card>
      )}

      {hasHealthInformation && (
        <Card>
          <CardBody>
            <SectionTitle>For your clinician</SectionTitle>

            {bookingInfo.allergies && (
              <DetailRow
                label="Allergies"
                value={bookingInfo.allergies}
                stacked
              />
            )}
            {bookingInfo.currentMedication && (
              <DetailRow
                label="Current Medication"
                value={bookingInfo.currentMedication}
                stacked
              />
            )}
          </CardBody>
        </Card>
      )}

      <Card ref={attestationRef}>
        <CardBody>
          <AttestationRow>
            <Checkbox checked={isAttested} onChange={handleAttestationChange} />
            <AttestationText>{ATTESTATION_TEXT}</AttestationText>
          </AttestationRow>
          {showAttestationError && (
            <InlineAlert>{ATTESTATION_ERROR}</InlineAlert>
          )}
        </CardBody>
      </Card>

      <TermsSection ref={termsRef}>
        <TermsAccordion
          groupTitle="Please open, read and tick to agree"
          sections={CANCELLATION_POLICY_SECTIONS}
          agreements={{ [POLICY_SECTION_KEY]: isPolicyAgreed }}
          onAgreementChange={handlePolicyAgreementChange}
          defaultOpenKeys={[POLICY_SECTION_KEY]}
          errorMessage={CANCELLATION_POLICY_NOT_AGREED_MESSAGE}
          showError={showPolicyError}
        />
      </TermsSection>

      <div ref={bookingErrorsRef}>
        {bookingErrors.length > 0 && (
          <InlineAlert>{bookingErrors.join(' ')}</InlineAlert>
        )}
      </div>

      <BookingActionBar
        amountLabel={payableAmountLabel}
        amount={payableDisplay}
        nextLabel="Confirm"
        onNext={handleConfirm}
        isLoading={isLoading}
      />
    </>
  );
};

export default Confirmation;
